import type { DefineQuestionnaireDefinitionInput } from "@repo/api/schemas";
import type { DraftItem, DraftSection } from "./types";

export function questionnaireSectionsInput(
  sections: readonly DraftSection[],
): DefineQuestionnaireDefinitionInput["sections"] {
  return sections.map(sectionInput);
}

function sectionInput(section: DraftSection): DefineQuestionnaireDefinitionInput["sections"][number] {
  const { clientId, kind: _kind, items, ...rest } = section;
  return { ...rest, clientRef: clientId, items: items.map(itemInput) };
}

function optionalText(value: string | undefined): string | undefined {
  return value?.trim() || undefined;
}

function itemInput(item: DraftItem): DefineQuestionnaireDefinitionInput["sections"][number]["items"][number] {
  if (item.kind === "RepeatingGroup") {
    const { clientId, items, ...rest } = item;
    type RepeatingItems = Extract<
      DefineQuestionnaireDefinitionInput["sections"][number]["items"][number],
      { kind: "RepeatingGroup" }
    >["items"];
    return { ...rest, clientRef: clientId, items: items.map(itemInput) as RepeatingItems };
  }
  if (item.kind === "Question" && item.value.kind === "Choice") {
    const { clientId, value, askYesNoFirst, help, ...rest } = item;
    return {
      ...rest,
      ...(optionalText(help) ? { help: optionalText(help) } : {}),
      ...(askYesNoFirst && value.cardinality === "Single" ? { askYesNoFirst: true } : {}),
      clientRef: clientId,
      value: {
        ...value,
        options: value.options.map(({ clientId: optionClientId, detail, ...option }) => ({
          ...option,
          ...(optionalText(detail) ? { detail: optionalText(detail) } : {}),
          clientRef: optionClientId,
        })),
      },
    };
  }
  const { clientId, ...rest } = item;
  if (rest.kind === "Question") {
    delete rest.askYesNoFirst;
    const help = optionalText(rest.help);
    if (help) rest.help = help;
    else delete rest.help;
  }
  return { ...rest, clientRef: clientId };
}
